import { Modal, Form, Input, Button, Row, Col, Switch, Select } from "antd";
import Draggable from "react-draggable";

import { formItemLayout, inputLayout2 } from "../../../../constants/ApplicationConstants/FormConstants";
import CircularProgressBar from "../../../common/CircularProgressBar";
import FormInputRules from "../../../../util/FormInputRules";
import { General } from "../../../../util/General";
import Loading from "../../../common/Loading";
import ImageUploader from "../../../common/ImageUploader";
import { createHandleEditKeyPress, createHandleKeyPress, createHandlePinCodeKeyPress } from "../../../../util/handleKeyPress";
import styles from "../../index.module.less";
import { useCustomerMaster } from "./index.hook";
import { useReactDraggable } from "../../../../hooks/useReactDraggable";

import type FixMeLater from "../../../../shared/types/fixMeLater.type";

type CustomerMasterProps = {
  saving: boolean;
  addRecord: boolean;
  handleAddRecord: (value: boolean) => void;
  rowData: FixMeLater;
  handleClearRowIdAndActionType: () => void;
  handleSubmit: (data: FixMeLater) => void;
};

const CustomerMaster = (props: CustomerMasterProps) => {
  const config = useCustomerMaster(props);

  const { bounds, onStart } = useReactDraggable();

  return (
    <Modal
      afterClose={() => config.form.resetFields()}
      destroyOnClose={true}
      maskClosable={false}
      centered
      title={<div className="modal-container">{`${props.addRecord ? "Add" : "Edit"} Customer Master`}</div>}
      open={true}
      onCancel={props.addRecord ? props.handleAddRecord.bind(this, false) : props.handleClearRowIdAndActionType}
      width={"80%"}
      okButtonProps={{
        style: {
          display: "none"
        }
      }}
      cancelButtonProps={{ style: { display: "none" } }}
      modalRender={(modal) => (
        <Draggable bounds={bounds} handle=".ant-modal-header" onStart={(event, uiData) => onStart(event as any, uiData)}>
          {modal}
        </Draggable>
      )}
    >
      {((props.addRecord && config.isOnAddLoading) || (!props.addRecord && !config.formFieldsLoadedToForm)) && (
        <div className={`${styles["textCenter"]}`}>
          <Loading />
        </div>
      )}

      {((props.addRecord && !config.isOnAddLoading) || (!props.addRecord && config.formFieldsLoadedToForm)) && (
        <Form
          ref={config.formRef}
          onValuesChange={(changedValues) => {
            const fieldName = Object.keys(changedValues)[0];
            config.form.validateFields([fieldName]);
          }}
          className={`${styles["formDiv"]} ${styles["formDivExtended"]}`}
          {...formItemLayout}
          {...formItemLayout}
          form={config.form}
          validateTrigger="onBlur"
          colon={false}
          name="register"
          onFinish={props.handleSubmit}
        >
          <Row gutter={24}>
            <Col span={12} xs={24} sm={24} md={24} lg={12}>
              <Form.Item {...inputLayout2} name="customer_name" label="Customer Name" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("customer name"), FormInputRules.ValidateFirstAndLastName()]}>
                <Input name="customer_name" />
              </Form.Item>

              <Form.Item {...inputLayout2} name="contact_person" label="Contact Person" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("contact person"), FormInputRules.ValidateFirstAndLastName()]}>
                <Input name="contact_person" />
              </Form.Item>
              {props.addRecord ? (
                <Form.Item {...inputLayout2} name="mobile_number" label="Contact Number " normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("contact number"), FormInputRules.ContactNumberValidation2Rule()]}>
                  <Input prefix="+91" name="mobile_number" onKeyPress={createHandleKeyPress()} />
                </Form.Item>
              ) : (
                <Form.Item {...inputLayout2} name="mobile_number" label="Contact Number " normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("contact number"), FormInputRules.ContactNumberValidationRule()]}>
                  <Input name="mobile_number" onKeyPress={createHandleEditKeyPress()} />
                </Form.Item>
              )}

              <Form.Item {...inputLayout2} name="email_address" label="Email Address" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.EmailValidationRule()]}>
                <Input name="email_address" />
              </Form.Item>

              <Form.Item {...inputLayout2} name="address" label="Corporate Address" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("address")]}>
                <Input name="address" />
              </Form.Item>

              <Form.Item {...inputLayout2} name="allowed_payment_gateway" label="Allowed Payment Gateway">
                <Select mode="multiple" allowClear placeholder="Please select" options={config.allowedPaymentGatewayOptionList} />
              </Form.Item>

              <Row>
                <Col span={12}>
                  <Form.Item {...inputLayout2} name="city" label="City" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("city"), FormInputRules.allowOnlyCharactersRule()]}>
                    <Input name="city" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item {...inputLayout2} name="state" label="State" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("state"), FormInputRules.allowOnlyCharactersRule()]}>
                    <Input name="state" />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={12}>
                  <Form.Item {...inputLayout2} name="country" label="Country" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("country"), FormInputRules.allowOnlyCharactersRule()]}>
                    <Input name="country" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item {...inputLayout2} name="pin_code" label="Pin Code" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("pin code"), FormInputRules.PinCodeValidationRule("Pin code")]}>
                    <Input name="pin_code" onKeyPress={createHandlePinCodeKeyPress()} />
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            <Col span={12} xs={24} sm={24} md={24} lg={12} style={{ height: "22rem" }}>
              {/* Only for add form case use */}
              {props.addRecord && (
                <Form.Item {...inputLayout2} name="isDefaultTheme" label="Set Theme">
                  <Switch checked={config.form.getFieldValue("isDefaultTheme")} onChange={config.handleSetDefaultTheme} checkedChildren="Default" unCheckedChildren="Custom" />
                </Form.Item>
              )}
              {config.loading && <Loading />}
              {!config.loading && (
                <>
                  <Form.Item {...inputLayout2} name="header_text" label="Header Text" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("header text")]}>
                    <Input name="header_text" disabled={props.addRecord && config.themeValue?.header_text} />
                  </Form.Item>
                  <Form.Item {...inputLayout2} name="footer_text" label="Footer Text" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("footer text")]}>
                    <Input name="footer_text" disabled={props.addRecord && config.themeValue?.footer_text} />
                  </Form.Item>
                  <Form.Item {...inputLayout2} name="primary_color" label="Primary Colour" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("primary color")]}>
                    <Input name="primary_color" disabled={props.addRecord && config.themeValue?.primary_color} />
                  </Form.Item>
                  <Form.Item {...inputLayout2} name="secondary_color" label="Secondary Colour" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("secondary color")]}>
                    <Input name="secondary_color" disabled={props.addRecord && config.themeValue?.secondary_color} />
                  </Form.Item>
                  <Form.Item
                    {...inputLayout2}
                    name="logo_url"
                    label="Logo Image"
                    rules={[
                      {
                        required: true,
                        message: "Please upload a logo image."
                      }
                    ]}
                  >
                    <ImageUploader form={config.form} imageFile={config.imageFile} setImageFile={config.setImageFile} imageUrl={config.imageUrl} themeValue={config.themeValue} />
                  </Form.Item>
                </>
              )}
            </Col>
          </Row>

          <Form.Item className={`${styles["form-itemFloatR20"]}`}>
            <Button htmlType="submit" className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`}>
              {props.saving ? <CircularProgressBar /> : props.addRecord ? "Submit" : "Update"}
            </Button>
          </Form.Item>

          <Form.Item className={`${styles["form-itemFloatR20"]}`}>
            <Button disabled={props.saving} className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`} onClick={props.addRecord ? props.handleAddRecord.bind(this, false) : props.handleClearRowIdAndActionType}>
              Close
            </Button>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default CustomerMaster;
